/** Formato visual con separador de miles (es-CO: 10.000.000). */
export function formatAmountWithDots(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('es-CO', { maximumFractionDigits: 0 });
}

/** Extrae solo dígitos del valor ingresado. */
export function parseAmountDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/** Formato moneda COP sin decimales. */
export function formatCop(value) {
  const n = Number(value);
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);
}

/** Saldo final tras n cuotas con total fijo T (capital + interés + seguro 0,2% sobre saldo). */
function endingBalanceForFixedTotal(principal, n, r, insuranceRate, totalPayment) {
  let balance = principal;
  for (let i = 0; i < n; i += 1) {
    const interest = r === 0 ? 0 : balance * r;
    const lifeInsurance = Math.round(balance * insuranceRate);
    const capital = totalPayment - interest - lifeInsurance;
    if (capital < 0) return -Infinity;
    balance -= capital;
  }
  return balance;
}

/** Cuota total fija (mismo valor todos los meses) con seguro = % del saldo inicial de cada cuota. */
function solveEqualTotalPayment(principal, n, r, insuranceRate) {
  let lo = Math.ceil(principal / n);
  let hi = lo;

  if (r === 0) {
    hi = Math.ceil(principal / n + principal * insuranceRate);
  } else {
    const factor = Math.pow(1 + r, n);
    const frenchPi = (principal * r * factor) / (factor - 1);
    hi = Math.ceil(frenchPi + principal * insuranceRate);
  }

  while (endingBalanceForFixedTotal(principal, n, r, insuranceRate, hi) > 0.5) {
    hi = Math.ceil(hi * 1.05) + 1;
    if (hi > principal * 10) break;
  }

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const end = endingBalanceForFixedTotal(principal, n, r, insuranceRate, mid);
    if (end > 0.5) lo = mid + 1;
    else hi = mid;
  }

  return lo;
}

function buildSchedule(principal, n, r, insuranceRate, equalTotalPayment) {
  const schedule = [];
  let balance = principal;
  let totalInterest = 0;
  let totalLifeInsurance = 0;
  let totalPaid = 0;

  for (let i = 1; i <= n; i += 1) {
    const openingBalance = balance;
    const interest = r === 0 ? 0 : balance * r;
    const lifeInsurance = Math.round(openingBalance * insuranceRate);
    const capital = equalTotalPayment - interest - lifeInsurance;
    balance = Math.max(0, openingBalance - capital);
    totalInterest += interest;
    totalLifeInsurance += lifeInsurance;
    totalPaid += equalTotalPayment;

    schedule.push({
      period: i,
      openingBalance,
      payment: interest + capital,
      interest,
      capital,
      lifeInsurance,
      totalPayment: equalTotalPayment,
      balance,
    });
  }

  return { schedule, totalInterest, totalLifeInsurance, totalPaid };
}

/** Simulación: cuota total igual todos los meses; seguro 0,2% mensual sobre saldo inicial. */
export function simulateCredit({ amount, months, monthlyRatePercent, lifeInsurancePercent = 0.2 }) {
  const principal = Number(amount);
  const n = Math.floor(Number(months));
  const ratePct = Number(monthlyRatePercent);

  if (!Number.isFinite(principal) || principal <= 0) {
    return { error: 'Ingresa un monto válido mayor a cero.' };
  }
  if (!Number.isFinite(n) || n < 1 || n > 360) {
    return { error: 'El plazo debe estar entre 1 y 360 meses.' };
  }
  if (!Number.isFinite(ratePct) || ratePct < 0 || ratePct > 100) {
    return { error: 'Ingresa una tasa de interés mensual válida (0–100 %).' };
  }

  const r = ratePct / 100;
  const insurancePct = Number(lifeInsurancePercent);
  const insuranceRate =
    Number.isFinite(insurancePct) && insurancePct > 0 ? insurancePct / 100 : 0;

  const equalTotalPayment =
    insuranceRate > 0
      ? solveEqualTotalPayment(principal, n, r, insuranceRate)
      : r === 0
        ? Math.ceil(principal / n)
        : (() => {
            const factor = Math.pow(1 + r, n);
            return Math.ceil((principal * r * factor) / (factor - 1));
          })();

  const { schedule, totalInterest, totalLifeInsurance, totalPaid } = buildSchedule(
    principal,
    n,
    r,
    insuranceRate,
    equalTotalPayment
  );

  return {
    monthlyPayment: equalTotalPayment,
    firstMonthTotal: equalTotalPayment,
    totalPaid,
    totalInterest,
    lifeInsurance: totalLifeInsurance,
    lifeInsurancePercent: insurancePct,
    grandTotal: totalPaid,
    schedule,
  };
}

/**
 * Extrae tasa mensual en % desde texto libre o número guardado en admin.
 * Acepta: "1.2", "1,5", "1.2% mensual", "Tasa 0.8% EA".
 */
export function parseRatePercentFromText(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return null;

  const normalized = raw.replace(',', '.');

  const withPercent = normalized.match(/(\d+(?:\.\d+)?)\s*%/);
  if (withPercent) {
    const n = Number(withPercent[1]);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
  }

  if (/^\d+(?:\.\d+)?$/.test(normalized)) {
    const n = Number(normalized);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
  }

  const firstNum = normalized.match(/(\d+(?:\.\d+)?)/);
  if (firstNum) {
    const n = Number(firstNum[1]);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
  }

  return null;
}

/** Intenta extraer plazo máximo en meses desde texto (ej. "Hasta 60 meses"). */
export function parseMaxMonthsFromText(text) {
  const raw = String(text ?? '').toLowerCase();
  const match = raw.match(/(\d+)\s*mes/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}
